import { memo, useMemo } from 'react';
import { MdOutlineComment, MdOutlineLink } from 'react-icons/md';
import { Tooltip } from '@ds';
import { AircraftIcon, CarGroundIcon, LightsAndSirens, SolidArrowIcon } from '@icons';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { TransportType } from '@lp/features/RoutePlanner/components/TransportType';
import {
  AviaLegTypes,
  GroundLegTypes,
  LegLocationTypeEnum,
  RouteLegTypeEnum,
  RouteLocationType,
  RouteTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import {
  convertMsToTimeString,
  getTimeDifference,
  pickLocationDisplayName,
  WithExtendedLocation,
} from '@/utils/helpers';
import { isGroundLegType } from '@/utils/route';
import { RouteSegmentType } from '@/types/route';
import { LocationMarker } from './LocationMarker';
import { ResourcesLegInfo } from './ResourcesLegInfo';
import { CommercialLegInfo } from './CommercialLegInfo';
import { GroundProviderLegInfo } from './GroundProviderLegInfo';
import { AirProviderLegInfo } from './AirProviderLegInfo';
import { TmdxLegInfo } from './TmdxLegInfo';
import { CurriLegInfo } from './CurriLegInfo';
import { FlightStopsTooltip } from './FlightStopsTooltip';
import { GroundTripLegInfo } from './GroundTripLegInfo';
import { GroundNotFeasibleWarning } from './GroundNotFeasibleWarning';

type SegmentLegInformationProps = {
  type?: 'logistics' | 'suggestion';
  routeType: RouteTypeEnum;
  leg: AviaLegTypes | GroundLegTypes;
  segment: RouteSegmentType;
  isLightsSirensRequired?: boolean;
  handleLegInfoClick?: (leg: AviaLegTypes | GroundLegTypes, segmentId: string | null, prevLegId: string | null) => void;
};

export const getMarkerName = (location: WithExtendedLocation<RouteLocationType> | undefined) => {
  if (!location) return 'N/A';

  switch (location.locationType) {
    case LegLocationTypeEnum.airport:
      return `${location.iata || location.icao}`;
    case LegLocationTypeEnum.hub:
      return location.code || 'HUB';
    case LegLocationTypeEnum.hospital:
      return `DH, ${pickLocationDisplayName(location)}`;
    case LegLocationTypeEnum.txcenter:
      return `TXC, ${pickLocationDisplayName(location)}`;
    case LegLocationTypeEnum.homeResidence:
    case LegLocationTypeEnum.custom:
      return pickLocationDisplayName(location);
    default:
      return `CUSTOM`;
  }
};

const getLocationType = (
  leg: AviaLegTypes | GroundLegTypes,
  type: 'start' | 'end',
): { type: 'start' | 'end'; segmentType: SegmentTypeEnum } => {
  if (leg.segmentType !== SegmentTypeEnum.custom) {
    return { type, segmentType: leg.segmentType };
  }

  const isStart = type === 'start';
  const locationType = isStart ? leg.departureLocation.locationType : leg.arrivalLocation.locationType;

  switch (locationType) {
    case LegLocationTypeEnum.hub:
      return { type, segmentType: isStart ? SegmentTypeEnum.hospital : SegmentTypeEnum.hub };
    case LegLocationTypeEnum.hospital:
      return { type, segmentType: isStart ? SegmentTypeEnum.txcenter : SegmentTypeEnum.hospital };
    case LegLocationTypeEnum.txcenter:
      return { type, segmentType: isStart ? SegmentTypeEnum.hub : SegmentTypeEnum.txcenter };
    default:
      return { type, segmentType: leg.segmentType };
  }
};

const checkOutlinedLocation = (type: LegLocationTypeEnum) =>
  [LegLocationTypeEnum.airport, LegLocationTypeEnum.homeResidence, LegLocationTypeEnum.custom].includes(type);

export const SegmentLegInformation = memo(
  ({ type, routeType, leg, segment, handleLegInfoClick, isLightsSirensRequired }: SegmentLegInformationProps) => {
    const { extendedLocationById } = useRouteContext();
    const { departureTime, arrivalTime, resources, departureLocation, arrivalLocation, hasScs } = leg;

    const isRepositionType = [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase].includes(segment.type);

    const timeDuration = useMemo(() => {
      const timeDifference = getTimeDifference(new Date(departureTime), new Date(arrivalTime));
      return convertMsToTimeString(timeDifference.MILLISECONDS);
    }, [departureTime, arrivalTime]);

    const startLocationVariant = checkOutlinedLocation(departureLocation.locationType) ? 'outlined' : 'filled';
    const endLocationVariant = checkOutlinedLocation(arrivalLocation.locationType) ? 'outlined' : 'filled';

    const isGround = isGroundLegType(leg);
    const icon = isGround ? <CarGroundIcon /> : <AircraftIcon />;
    const startLocationMarker = getLocationType(leg, 'start');
    const endLocationMarker = getLocationType(leg, 'end');
    const includesDevice = !!resources.find((r) => r.resourceType === CaseResourceType.OCSConsole) || hasScs;
    const toTxCenter = leg.arrivalLocation.locationType === LegLocationTypeEnum.txcenter;
    const isLogisticsView = type === 'logistics';

    const renderLegInfo = (currentLeg: AviaLegTypes | GroundLegTypes) => {
      switch (currentLeg.legType) {
        case RouteLegTypeEnum.TmdxJetAviaLeg:
          return <TmdxLegInfo leg={currentLeg} />;
        case RouteLegTypeEnum.CommercialFlightAviaLeg:
          return <CommercialLegInfo bookingNumber={currentLeg.bookingNumber} />;
        case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
        case RouteLegTypeEnum.TxCenterProviderGroundLeg:
          return <GroundProviderLegInfo leg={currentLeg} />;
        case RouteLegTypeEnum.SelfServicedGroundLeg:
          return !currentLeg.isFeasibleForDrive && <GroundNotFeasibleWarning />;
        case RouteLegTypeEnum.SupplementalLiftAviaLeg:
        case RouteLegTypeEnum.TxCenterProviderAviaLeg:
          return <AirProviderLegInfo leg={currentLeg} />;
        case RouteLegTypeEnum.CurriServiceGroundLeg:
          return <CurriLegInfo trackingLink={currentLeg.trackingNumber} />;
        case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
        case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
          return (
            isLogisticsView && (
              <GroundTripLegInfo
                tripId={currentLeg.tripReference?.tripId}
                isFeasibleForDrive={currentLeg.isFeasibleForDrive}
              />
            )
          );
        default:
          return null;
      }
    };

    const showResources = !!resources.length || hasScs;

    const startLocation = extendedLocationById.get(leg.departureLocation.locationId);
    const endLocation = extendedLocationById.get(leg.arrivalLocation.locationId);

    return (
      <div className="flex flex-col -translate-y-1.5 mb-8 grow">
        <div className="flex gap-2 items-center pb-3 w-full border-b-1 border-base-55">
          <button
            onClick={() => handleLegInfoClick?.(leg, segment.id, leg.chainPosition.previousLegId)}
            className="flex gap-2 items-center"
          >
            <LocationMarker
              variant={startLocationVariant}
              type={startLocationMarker.type}
              segmentType={startLocationMarker.segmentType}
              useMonoFont
            >
              {!!startLocation && getMarkerName(startLocation)}
            </LocationMarker>
            <div className="flex gap-2 items-center shrink-0">
              <div className="w-3 border-b-1 border-base-5" />
              <div className="flex items-center gap-1">
                {leg.legType === RouteLegTypeEnum.CommercialFlightAviaLeg && !!leg.sabreGroupId && (
                  <Tooltip content={<FlightStopsTooltip routeType={routeType} sabreGroupId={leg.sabreGroupId} />}>
                    <div className="text-base-5">
                      <MdOutlineLink size={18} />
                    </div>
                  </Tooltip>
                )}
                <TransportType legType={leg.legType} />
                {isGround && isLightsSirensRequired && includesDevice && toTxCenter && (
                  <span className="px-1 py-0.5 text-xs-m text-info-40 flex justify-center items-center">
                    <LightsAndSirens height={16} width={16} />
                  </span>
                )}
                {icon}
              </div>
              {isRepositionType && (
                <span className="text-s-m text-base-10">
                  {segment.type === SegmentTypeEnum.reposition ? 'Reposition' : 'Return to base'}
                </span>
              )}
              <span className="text-s-sb text-base-10">{timeDuration}</span>
              <SolidArrowIcon />
            </div>
            <LocationMarker
              variant={endLocationVariant}
              type={endLocationMarker.type}
              segmentType={endLocationMarker.segmentType}
              useMonoFont
            >
              {!!endLocation && getMarkerName(endLocation)}
            </LocationMarker>
          </button>
          {!!leg.comments && (
            <Tooltip showArrow content={leg.comments} classNames={{ base: 'max-w-120' }}>
              <div className="cursor-pointer">
                <MdOutlineComment size={18} />
              </div>
            </Tooltip>
          )}
        </div>
        <div className="flex flex-col gap-1 mt-2 pb-2 w-full has-[*]:border-b-1 has-[*]:border-base-55">
          {renderLegInfo(leg)}
          {showResources && <ResourcesLegInfo legType={leg.legType} hasScs={hasScs} resources={resources} />}
        </div>
      </div>
    );
  },
);
